import { motion } from "framer-motion";
import { Heart, Sparkles, Users, Shield } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const OurPhilosophySection = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      badge: "OUR PHILOSOPHY",
      title: "Treatments for",
      titleAccent: "Everyone",
      subtitle: "Men & Women Welcome",
      description: "At Karine Lauzon RN Clinics, we believe everyone deserves to feel confident in their own skin. Our treatments are designed to be subtle yet effective — enhancing your natural beauty while maintaining your authentic self.",
      philosophy: "We're here to offer positive treatment solutions that bring out the best version of you. Whether you're looking to refresh your appearance or address specific concerns, our approach focuses on natural-looking results that boost your confidence and self-esteem.",
      values: [
        {
          icon: Users,
          title: "For Everyone",
          description: "We proudly serve clients of all genders, ages, and backgrounds"
        },
        {
          icon: Sparkles,
          title: "Subtle & Natural",
          description: "Enhancements that look natural — your secret to looking refreshed"
        },
        {
          icon: Heart,
          title: "Confidence Focused",
          description: "Our goal is to help you feel your absolute best, inside and out"
        },
        {
          icon: Shield,
          title: "Safe & Trusted",
          description: "Expert care with your comfort and safety as our top priority"
        }
      ]
    },
    fr: {
      badge: "NOTRE PHILOSOPHIE",
      title: "Traitements pour",
      titleAccent: "Tous",
      subtitle: "Hommes & Femmes Bienvenus",
      description: "Chez Karine Lauzon RN Clinics, nous croyons que tout le monde mérite de se sentir confiant dans sa peau. Nos traitements sont conçus pour être subtils mais efficaces — mettant en valeur votre beauté naturelle tout en préservant votre authenticité.",
      philosophy: "Nous sommes ici pour offrir des solutions de traitement positives qui font ressortir la meilleure version de vous-même. Que vous cherchiez à rafraîchir votre apparence ou à répondre à des préoccupations spécifiques, notre approche se concentre sur des résultats naturels qui renforcent votre confiance et votre estime de soi.",
      values: [
        {
          icon: Users,
          title: "Pour Tous",
          description: "Nous servons fièrement des clients de tous genres, âges et origines"
        },
        {
          icon: Sparkles,
          title: "Subtil & Naturel",
          description: "Des améliorations naturelles — votre secret pour paraître rafraîchi"
        },
        {
          icon: Heart,
          title: "Axé sur la Confiance",
          description: "Notre objectif est de vous aider à vous sentir au mieux"
        },
        {
          icon: Shield,
          title: "Sûr & Fiable",
          description: "Soins experts avec votre confort et sécurité comme priorité"
        }
      ]
    }
  };

  const t = content[language] || content.en;

  return (
    <section className="py-16 px-6 bg-gradient-to-b from-background to-primary/5 overflow-hidden" data-testid="philosophy-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-mono text-xs text-primary tracking-widest">
              {t.badge}
            </span>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t.title} <span className="italic text-primary">{t.titleAccent}</span>
            </h2>
            <p className="text-xl text-amber-600 font-medium mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t.subtitle}
            </p>
            
            <p className="text-muted-foreground mb-4 leading-relaxed">
              {t.description}
            </p>
            <p className="text-muted-foreground leading-relaxed">
              {t.philosophy}
            </p>

            {/* Decorative Element */}
            <div className="mt-8 flex items-center gap-4">
              <div className="h-px flex-1 bg-gradient-to-r from-primary/50 to-transparent" />
              <Sparkles className="w-5 h-5 text-primary" />
              <div className="h-px flex-1 bg-gradient-to-l from-primary/50 to-transparent" />
            </div>
          </motion.div>

          {/* Right - Values Grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 gap-4"
          >
            {t.values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                className="bg-white/80 backdrop-blur-sm rounded-xl p-5 shadow-lg border border-primary/10 hover:shadow-xl hover:border-primary/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-amber-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <value.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Bottom Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-2xl p-8 text-center relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-40 h-40 bg-amber-500 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-primary rounded-full blur-3xl" />
          </div>
          
          <div className="relative z-10">
            <p className="text-white/90 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
              {language === "fr" 
                ? "\"Votre beauté, sublimée naturellement. Votre confiance, notre mission.\""
                : "\"Your beauty, naturally enhanced. Your confidence, our mission.\""}
            </p>
            <p className="mt-4 text-amber-400 font-medium">
              — Karine Lauzon, RN
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default OurPhilosophySection;
